import { EyeOutlined, SearchOutlined } from "@ant-design/icons";
import { Button, Input, Table, TableColumnsType, Tag, Typography } from "antd";
import { debounce } from "lodash";
import React, { useCallback, useState } from "react";
import ModalViewTransfer from "../../components/modal/Warehouse/ModalViewTransfer";
import { IPartNumberTransfer } from "../../models/Warehouse";
import { useGetTransferWarehouse } from "../../query/WarehouseQuery";

const { Text } = Typography;

const WarehouseListTransfer: React.FC = () => {
  const [query, setQuery] = useState<{
    page: number;
    limit: number;
    search?: string;
  }>({
    page: 1,
    limit: 10,
  });

  const [modalState, setModalState] = useState<{
    open: boolean;
    transferId: number | null;
  }>({
    open: false,
    transferId: null,
  });

  const { data, isLoading } = useGetTransferWarehouse(query);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleSearch = useCallback(
    debounce((value: string) => {
      setQuery({ ...query, page: 1, search: value || undefined });
    }, 500),
    [],
  );

  const columns: TableColumnsType<IPartNumberTransfer> = [
    {
      title: "Tên phiếu",
      dataIndex: "transferName",
      key: "transferName",
      width: 200,
      filterDropdown: () => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Tìm kiếm tên phiếu"
            onChange={(e) => handleSearch(e.target.value)}
            style={{ marginBottom: 8, display: "block" }}
          />
        </div>
      ),
      filterIcon: () => (
        <SearchOutlined
          style={{ color: query.search ? "#1890ff" : undefined }}
        />
      ),
      render: (text: string) => (
        <Text strong className="text-blue-600">
          {text}
        </Text>
      ),
    },
    {
      title: "Vị trí đích",
      dataIndex: ["targetLocation", "code"],
      key: "targetLocation",
      width: 150,
      align: "center",
      render: (text: string) => (
        <Tag color="blue" className="font-medium">
          {text}
        </Tag>
      ),
    },
    {
      title: "Trạng thái nhận",
      dataIndex: "isCompleted",
      key: "isCompleted",
      width: 150,
      align: "center",
      filters: [
        { text: "Đã hoàn tất", value: true },
        { text: "Đang chờ xác nhận", value: false },
      ],
      onFilter: (value: any, record: IPartNumberTransfer) =>
        record.isCompleted === value,
      render: (isCompleted: boolean) => (
        <Tag color={isCompleted ? "success" : "warning"}>
          {isCompleted ? "Kho xuất đã nhận hàng" : "Đang chờ xác nhận"}
        </Tag>
      ),
    },
    {
      title: "Trạng thái xuất",
      dataIndex: "isExported",
      key: "isExported",
      width: 150,
      align: "center",
      filters: [
        { text: "Đã xuất", value: true },
        { text: "Chưa xuất", value: false },
      ],
      onFilter: (value: any, record: IPartNumberTransfer) =>
        record.isExported === value,
      render: (isExported: boolean) => (
        <Tag color={isExported ? "success" : "warning"}>
          {isExported ? "Kho đã xuất" : "Chưa xuất"}
        </Tag>
      ),
    },
    {
      title: "Ghi chú",
      dataIndex: "note",
      key: "note",
      width: 250,
      ellipsis: true,
      render: (text: string) => (
        <Text type="secondary">{text || "Chưa có ghi chú"}</Text>
      ),
    },
    {
      title: "Thời gian tạo",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 180,
      align: "center",
      sorter: (a: IPartNumberTransfer, b: IPartNumberTransfer) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      render: (date: Date) => (
        <Text>{new Date(date).toLocaleString("vi-VN")}</Text>
      ),
    },
    {
      title: "Thao tác",
      key: "action",
      width: 120,
      align: "center",
      fixed: "right",
      render: (_: any, record: IPartNumberTransfer) => (
        <Button
          type="primary"
          size="small"
          icon={<EyeOutlined />}
          onClick={() => setModalState({ open: true, transferId: record.id })}
        >
          Chi tiết
        </Button>
      ),
    },
  ];

  return (
    <>
      <Table
        columns={columns}
        dataSource={data?.data}
        loading={isLoading}
        rowKey="id"
        pagination={{
          current: data?.pagination.page,
          pageSize: data?.pagination.limit,
          total: data?.pagination?.total || 0,
          showSizeChanger: true,
          showQuickJumper: true,
          pageSizeOptions: [10, 20, 50, 100],
          showTotal: (total) => `Tổng ${total} phiếu`,
          onChange: (newPage, newPageSize) => {
            setQuery({ ...query, page: newPage, limit: newPageSize });
          },
        }}
      />

      <ModalViewTransfer
        open={modalState.open}
        onClose={() => setModalState({ open: false, transferId: null })}
        transferId={modalState.transferId}
      />
    </>
  );
};

export default WarehouseListTransfer;
